/**
 * Scrollbars that appear while an area scrolls and fade away once it stops.
 * The thumb colour is the registered custom property --nb-sb-thumb (base.css);
 * it is animated with the Web Animations API so it never overrides an
 * element's own CSS transitions.
 */

const SHOW_MS = 140;
const HIDE_MS = 450;
const IDLE_MS = 900;

const THUMB = "--nb-sb-thumb";

export function installAutoHideScrollbars(doc: Document = document): () => void {
  const view = doc.defaultView;
  if (!view) return () => {};

  const fades = new WeakMap<Element, Animation>();
  const timers = new Map<Element, number>();
  const reducedMotion = view.matchMedia?.("(prefers-reduced-motion: reduce)");

  const fade = (el: Element, show: boolean) => {
    if (typeof el.animate !== "function") return;
    const style = view.getComputedStyle(el);
    const from = style.getPropertyValue(THUMB).trim() || "transparent";
    const to = show ? style.getPropertyValue("--nb-sb-color").trim() : "transparent";
    fades.get(el)?.cancel();
    fades.set(
      el,
      el.animate([{ [THUMB]: from }, { [THUMB]: to }], {
        duration: reducedMotion?.matches ? 0 : show ? SHOW_MS : HIDE_MS,
        easing: "ease-out",
        fill: "forwards",
      }),
    );
  };

  const onScroll = (event: Event) => {
    const target = event.target;
    const el = target instanceof Element ? target : doc.scrollingElement;
    if (!el) return;
    const pending = timers.get(el);
    if (pending === undefined) fade(el, true);
    else view.clearTimeout(pending);
    timers.set(
      el,
      view.setTimeout(() => {
        timers.delete(el);
        fade(el, false);
      }, IDLE_MS),
    );
  };

  doc.addEventListener("scroll", onScroll, { capture: true, passive: true });
  return () => {
    doc.removeEventListener("scroll", onScroll, { capture: true });
    for (const id of timers.values()) view.clearTimeout(id);
    timers.clear();
  };
}
